/*
  Team & corporate page behaviour. Two small jobs:

  1. Show a sticky "Request a quote" button once the form has scrolled out of view. The button is a
     real anchor to #quote, so it works whether or not this file loads.
  2. Rewrite the contact form's hidden Subject on submit. Every quote used to arrive with a
     byte-identical subject line, so the inbox could not be sorted or scanned.
*/
(function () {
  'use strict';

  function stickyCta(bar) {
    if (bar.dataset.ready === 'true') return;
    bar.dataset.ready = 'true';
    var targets = [document.getElementById('quote'), document.getElementById('bulk-order'), document.querySelector('.corporate__hero-cta')].filter(Boolean);
    if (!targets.length) return;
    var scheduled = false;
    function paint() {
      scheduled = false;
      bar.hidden = targets.some(function (target) {
        var rect = target.getBoundingClientRect();
        return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      });
    }
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(paint);
    }
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    if (window.IntersectionObserver) {
      var observer = new IntersectionObserver(schedule);
      targets.forEach(function (target) { observer.observe(target); });
    }
    paint();
  }

  function quoteForm(form) {
    if (form.dataset.corporateReady === 'true') return;
    form.dataset.corporateReady = 'true';
    var subject = form.querySelector('[data-quote-subject]');
    var config = form.querySelector('[data-quote-copy]');
    var copy = {};
    try { copy = JSON.parse(config?.textContent || '{}'); } catch (_) {}
    var storageKey = 'jaxQuoteDraft';
    var lifetime = 30 * 60 * 1000;
    var draftNames = ['contact[Company or team]', 'contact[Quantity]', 'contact[Product]', 'contact[Same on every piece]', 'contact[Needed by]', 'contact[Phone]', 'contact[Bulk configuration]'];
    var referenceField = form.querySelector('[data-quote-reference]');
    var reference = '';
    var started = false;
    var waitTimer;
    var submit = form.querySelector('[type="submit"]');
    var status = form.querySelector('[data-quote-status]');
    var error = form.querySelector('[data-corporate-error]');
    var submitLabel = submit?.querySelector('[data-quote-submit-label]');
    var originalLabel = submitLabel?.textContent;
    var success = form.querySelector('[data-quote-success]');
    var field = name => form.querySelector('[name="' + name + '"]');
    function readDraft() {
      try {
        var saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (!saved || saved.version !== 2 || !Number.isFinite(saved.at) || Date.now() - saved.at >= lifetime || saved.at > Date.now() || !saved.fields || typeof saved.fields !== 'object' || Array.isArray(saved.fields)) {
          sessionStorage.removeItem(storageKey);
          return null;
        }
        return saved;
      } catch (_) { try { sessionStorage.removeItem(storageKey); } catch (_) {} return null; }
    }
    function validReference(value) { return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value || ''); }
    function emit(action, flow) {
      try { form.dispatchEvent(new CustomEvent('bulk-quote:action', { bubbles: true, detail: { action: action, ...(validReference(flow) ? { flow_id: flow } : {}) } })); } catch (_) {}
    }
    function ensureReference() {
      if (!validReference(reference)) {
        try { reference = crypto.randomUUID(); } catch (_) { reference = ''; }
      }
      if (referenceField) referenceField.value = reference;
      return reference;
    }
    function resetPending() {
      clearTimeout(waitTimer);
      delete form.dataset.submitting;
      form.removeAttribute('aria-busy');
      if (submit) { submit.disabled = false; submit.removeAttribute('aria-busy'); }
      if (submitLabel) submitLabel.textContent = originalLabel;
      if (status) { status.hidden = true; status.textContent = ''; }
    }
    function fitTextarea(control) {
      if (control?.tagName !== 'TEXTAREA') return;
      control.style.height = 'auto';
      control.style.height = Math.max(72, control.scrollHeight + Math.max(0, control.offsetHeight - control.clientHeight)) + 'px';
    }
    function fitTextareas() { form.querySelectorAll('textarea').forEach(fitTextarea); }
    function syncSelection() {
      var selection = form.querySelector('[data-quote-selection]');
      var summary = form.querySelector('[data-bulk-quote-summary]');
      if (selection) selection.hidden = !summary || summary.hidden || !summary.textContent.trim();
    }
    function clearInvalid() {
      Array.from(form.querySelectorAll('[aria-invalid="true"]')).forEach(function (control) {
        control.removeAttribute('aria-invalid');
        var ids = (control.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== 'CorporateFormError');
        if (ids.length) control.setAttribute('aria-describedby', ids.join(' ')); else control.removeAttribute('aria-describedby');
      });
      if (error) error.hidden = true;
    }
    function saveDraft() {
      var values = {};
      draftNames.forEach(function (name) { var control = field(name); if (control) values[name] = control.value; });
      try { sessionStorage.setItem(storageKey, JSON.stringify({ version: 2, at: Date.now(), reference: ensureReference(), submitted: true, fields: values })); } catch (_) {}
    }
    function announceStarted() {
      if (started) return;
      started = true;
      emit('started', ensureReference());
    }
    var saved = readDraft();
    if (success) {
      if (saved?.submitted && validReference(saved.reference)) {
        var receiptKey = 'jaxQuoteReceipt';
        var alreadyRecorded = false;
        try { alreadyRecorded = JSON.parse(sessionStorage.getItem(receiptKey) || 'null')?.reference === saved.reference; sessionStorage.setItem(receiptKey, JSON.stringify({ reference: saved.reference, at: Date.now() })); } catch (_) {}
        var receipt = success.querySelector('[data-quote-receipt]');
        if (receipt && typeof copy.reference === 'string') { receipt.textContent = copy.reference.replace('{{ reference }}', saved.reference); receipt.hidden = false; }
        if (!alreadyRecorded) emit('submitted', saved.reference);
      } else {
        try {
          var previousReceipt = JSON.parse(sessionStorage.getItem('jaxQuoteReceipt') || 'null');
          if (validReference(previousReceipt?.reference) && Number.isFinite(previousReceipt.at) && previousReceipt.at <= Date.now() && Date.now() - previousReceipt.at < lifetime) {
            var previousLabel = success.querySelector('[data-quote-receipt]');
            if (previousLabel && typeof copy.reference === 'string') { previousLabel.textContent = copy.reference.replace('{{ reference }}', previousReceipt.reference); previousLabel.hidden = false; }
          } else sessionStorage.removeItem('jaxQuoteReceipt');
        } catch (_) {}
      }
      try { sessionStorage.removeItem(storageKey); } catch (_) {}
      return;
    }
    if (!subject) return;
    if (form.querySelector('[data-quote-server-errors]') && saved) {
      reference = validReference(saved.reference) ? saved.reference : '';
      draftNames.forEach(function (name) {
        var control = field(name), value = saved.fields[name];
        if (!control || typeof value !== 'string' || value.length > 10000) return;
        if (control.tagName === 'SELECT' && !Array.from(control.options).some(option => option.value === value)) return;
        control.value = value;
      });
      var summary = form.querySelector('[data-bulk-quote-summary]');
      var configuration = field('contact[Bulk configuration]');
      if (summary && configuration?.value) { summary.textContent = configuration.value; summary.hidden = false; }
      var optional = form.querySelector('[data-quote-optional]');
      if (optional && [field('contact[Company or team]'), field('contact[Phone]')].some(control => control?.value)) optional.open = true;
    }
    fitTextareas();
    syncSelection();
    function view() { emit('viewed', ensureReference()); }
    if (window.IntersectionObserver) {
      var observer = new IntersectionObserver(function (entries) { if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); view(); } });
      observer.observe(form);
    } else view();
    form.addEventListener('input', function (event) {
      if (event.isTrusted) announceStarted();
      var control = event.target;
      if (control?.getAttribute('aria-invalid') === 'true' && control.validity?.valid) {
        control.removeAttribute('aria-invalid');
        var ids = (control.getAttribute('aria-describedby') || '').split(' ').filter(id => id && id !== 'CorporateFormError');
        if (ids.length) control.setAttribute('aria-describedby', ids.join(' ')); else control.removeAttribute('aria-describedby');
      }
      fitTextarea(control);
    });
    form.addEventListener('change', function (event) { if (event.isTrusted) announceStarted(); });
    form.addEventListener('bulk-quote:prefill', function () { if (!form.dataset.submitting) { syncSelection(); clearInvalid(); announceStarted(); } });

    form.addEventListener('submit', function (event) {
      if (event.defaultPrevented) return;
      if (form.dataset.submitting === 'true') { event.preventDefault(); return; }
      clearInvalid();
      var invalid = Array.from(form.querySelectorAll('input, select, textarea')).filter(function (field) { return field.willValidate && !field.validity.valid; });
      if (error) error.hidden = !invalid.length;
      if (invalid.length) {
        event.preventDefault();
        invalid.forEach(function (field) {
          field.setAttribute('aria-invalid', 'true');
          var describedBy = (field.getAttribute('aria-describedby') || '').split(' ').filter(Boolean);
          if (!describedBy.includes('CorporateFormError')) describedBy.push('CorporateFormError');
          field.setAttribute('aria-describedby', describedBy.join(' '));
        });
        var optional = invalid[0].closest('details');
        if (optional) optional.open = true;
        invalid[0].focus();
        emit('validation_error', reference);
        return;
      }
      if (navigator.onLine === false) {
        event.preventDefault();
        if (status) { status.hidden = false; status.textContent = copy.offline; }
        return;
      }
      var value = function (name) {
        var field = form.querySelector('[name="contact[' + name + ']"]');
        return field && field.value ? field.value.trim() : '';
      };

      var parts = ['Quote'];
      var company = value('Company or team');
      var quantity = value('Quantity');
      var needed = value('Needed by');

      if (company) parts.push(company);
      if (quantity) parts.push(quantity);
      if (needed) parts.push('needed ' + needed);

      subject.value = parts.join(' — ');
      announceStarted();
      saveDraft();
      form.dataset.submitting = 'true';
      if (submit) { submit.disabled = true; submit.setAttribute('aria-busy', 'true'); }
      if (submitLabel) submitLabel.textContent = copy.sending || originalLabel;
      if (status) { status.hidden = false; status.textContent = copy.sending_help; }
      waitTimer = setTimeout(function () { if (form.dataset.submitting && status) status.textContent = copy.waiting; }, 15000);
    });
    window.addEventListener('pageshow', function () { resetPending(); fitTextareas(); });
    window.addEventListener('resize', fitTextareas);
    window.addEventListener('pagehide', function () { clearTimeout(waitTimer); });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-corporate-sticky]'), stickyCta);
    Array.prototype.forEach.call(document.querySelectorAll('.corporate__form'), quoteForm);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
